import React from 'react';
import { ArrowDown, ArrowUp, CheckCircle, Globe, Lock, Search, XCircle } from 'lucide-react';
import { OrganizationItem } from './types';

export interface RepositoryFiltersProps {
  searchQuery: string;
  onSearchChange: (value: string) => void;
  availableOrganizations: OrganizationItem[];
  selectedOrganization: string;
  setSelectedOrganization: (v: string) => void;
  availableLanguages: string[];
  selectedLanguage: string;
  setSelectedLanguage: (v: string) => void;
  selectedVisibility: string;
  setSelectedVisibility: (v: string) => void;
  selectedScanStatus: string;
  setSelectedScanStatus: (v: string) => void;
  sortBy: string;
  setSortBy: (v: string) => void;
  sortOrder: 'asc' | 'desc';
  setSortOrder: (v: 'asc' | 'desc' | ((prev: 'asc' | 'desc') => 'asc' | 'desc')) => void;
  filteredCount: number;
  totalCount: number;
  onClearAll: () => void;
}

const visibilityOptions = [
  {
    value: '',
    label: 'All',
    icon: null,
    active: 'bg-white dark:bg-gray-600 text-gray-800 dark:text-gray-100 shadow-sm',
  },
  {
    value: 'public',
    label: 'Public',
    icon: Globe,
    active: 'bg-teal-100 dark:bg-teal-900/50 text-teal-700 dark:text-teal-400',
  },
  {
    value: 'private',
    label: 'Private',
    icon: Lock,
    active: 'bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-400',
  },
] as const;

const scanStatusOptions = [
  {
    value: '',
    label: 'All repos',
    icon: null,
    active: 'bg-white dark:bg-gray-600 text-gray-800 dark:text-gray-100 shadow-sm',
  },
  {
    value: 'scanned',
    label: 'Analyzed',
    icon: CheckCircle,
    active: 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-400',
  },
  {
    value: 'not_scanned',
    label: 'Not yet',
    icon: null,
    active: 'bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-400',
  },
] as const;

export const RepositoryFilters: React.FC<RepositoryFiltersProps> = ({
  searchQuery,
  onSearchChange,
  availableOrganizations,
  selectedOrganization,
  setSelectedOrganization,
  availableLanguages,
  selectedLanguage,
  setSelectedLanguage,
  selectedVisibility,
  setSelectedVisibility,
  selectedScanStatus,
  setSelectedScanStatus,
  sortBy,
  setSortBy,
  sortOrder,
  setSortOrder,
  filteredCount,
  totalCount,
  onClearAll,
}) => {
  const hasActiveFilters =
    searchQuery ||
    selectedOrganization ||
    selectedVisibility ||
    selectedLanguage ||
    selectedScanStatus;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex-1 min-w-[200px] relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search repositories..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
          />
        </div>

        {availableOrganizations.length > 0 && (
          <select
            value={selectedOrganization}
            onChange={(e) => setSelectedOrganization(e.target.value)}
            className="w-auto px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent cursor-pointer"
          >
            <option value="">All organizations</option>
            {availableOrganizations.map((org) => (
              <option
                key={org.login}
                value={org.login}
                disabled={org.accessStatus === 'restricted' || org.accessStatus === 'blocked'}
              >
                {org.login}
                {org.accessStatus === 'personal' || org.accessStatus === 'active'
                  ? ` (${org.repoCount})`
                  : ''}
                {org.accessStatus === 'restricted' ? ' (no access)' : ''}
                {org.accessStatus === 'blocked' ? ' (requires admin approval)' : ''}
              </option>
            ))}
          </select>
        )}

        {availableLanguages.length > 0 && (
          <select
            value={selectedLanguage}
            onChange={(e) => setSelectedLanguage(e.target.value)}
            className="w-auto px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent cursor-pointer"
          >
            <option value="">All languages</option>
            {availableLanguages.map((l) => (
              <option key={l} value={l}>
                {l}
              </option>
            ))}
          </select>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-0.5 p-1 bg-gray-100 dark:bg-gray-700/60 rounded-lg">
          {visibilityOptions.map(({ value, label, icon: Icon, active }) => (
            <button
              key={value || 'all'}
              onClick={() => setSelectedVisibility(value)}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-all ${
                selectedVisibility === value
                  ? active
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
              }`}
            >
              {Icon && <Icon className="w-3.5 h-3.5" />}
              {label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-0.5 p-1 bg-gray-100 dark:bg-gray-700/60 rounded-lg">
          {scanStatusOptions.map(({ value, label, icon: Icon, active }) => (
            <button
              key={value || 'all'}
              onClick={() => setSelectedScanStatus(value)}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-all ${
                selectedScanStatus === value
                  ? active
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
              }`}
            >
              {Icon && <Icon className="w-3.5 h-3.5" />}
              {label}
            </button>
          ))}
        </div>

        <div className="h-5 w-px bg-gray-200 dark:bg-gray-600" aria-hidden="true" />

        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="w-auto px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs focus:ring-2 focus:ring-teal-500 focus:border-transparent cursor-pointer"
          aria-label="Sort by"
        >
          <option value="name">Name</option>
          <option value="date">Date created</option>
          <option value="stars">Stars</option>
        </select>
        <button
          onClick={() => setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))}
          className="p-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:text-teal-600 dark:hover:text-teal-400 hover:border-teal-400 dark:hover:border-teal-500 transition-colors"
          aria-label={
            sortOrder === 'asc'
              ? 'Sort ascending — click for descending'
              : 'Sort descending — click for ascending'
          }
          title={sortOrder === 'asc' ? 'Ascending' : 'Descending'}
        >
          {sortOrder === 'asc' ? (
            <ArrowUp className="w-3.5 h-3.5" />
          ) : (
            <ArrowDown className="w-3.5 h-3.5" />
          )}
        </button>

        {filteredCount !== totalCount && (
          <span className="ml-1 text-xs text-gray-400 dark:text-gray-500 tabular-nums">
            {filteredCount} of {totalCount}
          </span>
        )}

        {hasActiveFilters && (
          <button
            onClick={onClearAll}
            className="flex items-center gap-1 px-2 py-1 text-xs text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
            title="Clear all filters"
          >
            <XCircle className="w-3.5 h-3.5" />
            Clear
          </button>
        )}
      </div>
    </div>
  );
};
